import { Page } from '../../base/Page';
import { useProducts } from '../../base/useDbHooks';
import { useBatchProductPricing, useCustomerPreOrders } from '../../base/useDbPreOrderHooks';
import { Card, Table } from 'flowbite-react';
import { useMemo } from 'react';
import { format } from 'date-fns';
import { DataSnapshot } from 'firebase/database';

export default function PreOrderList() {
    const { products } = useProducts();
    const { customerPreOrders, loading } = useCustomerPreOrders();

    // Memoize product IDs for pricing fetch
    const preOrderProductIds = useMemo(
        () =>
            Array.from(
                new Set(
                    customerPreOrders.flatMap((preOrder) =>
                        preOrder.items.map((item) => item.productId),
                    ),
                ),
            ),
        [customerPreOrders],
    );

    // Fetch pricing for all pre-order product IDs
    const { pricingSnapshotsMap } = useBatchProductPricing(preOrderProductIds);

    return (
        <Page title="Pre-Order List">
            <div className="container mx-auto py-8">
                <h1 className="uppercase text-2xl font-bold text-center mb-6">
                    Customer Pre-Orders
                </h1>
                <Card>
                    {loading ? (
                        <div>Loading...</div>
                    ) : (
                        <Table>
                            <Table.Head>
                                <Table.HeadCell>Customer</Table.HeadCell>
                                <Table.HeadCell>Items</Table.HeadCell>
                                <Table.HeadCell>Total Quantity</Table.HeadCell>
                                <Table.HeadCell>Pre-Order Date</Table.HeadCell>
                                <Table.HeadCell>Contact</Table.HeadCell>
                            </Table.Head>
                            <Table.Body>
                                {customerPreOrders.map((preOrder) => {
                                    const customerName = `${preOrder.firstName} ${preOrder.lastName}`;
                                    const preOrderDate = format(
                                        new Date(preOrder.preOrderDate),
                                        'PPp',
                                    );

                                    return (
                                        <Table.Row key={preOrder.preOrderId}>
                                            <Table.Cell>
                                                <div>{customerName}</div>
                                                <div className="text-sm text-gray-500">
                                                    {preOrder.userId}
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                {preOrder.items.map((item, index) => {
                                                    const product = products.find(
                                                        (p) => p.productId === item.productId,
                                                    );
                                                    const pricingSnapshots =
                                                        pricingSnapshotsMap.get(item.productId) || [];
                                                    const pricing = pricingSnapshots
                                                        .filter(
                                                            (snapshot): snapshot is DataSnapshot & {
                                                                key: string;
                                                            } => snapshot.key !== null,
                                                        )
                                                        .find((snapshot) => snapshot.key === item.pricingId)
                                                        ?.val();

                                                    return (
                                                        <div key={index} className="mb-2">
                                                            <div>
                                                                {product?.productNameEn || 'Unknown Product'} x{' '}
                                                                {item.quantity}
                                                            </div>
                                                            <div className="text-sm text-gray-500">
                                                                {pricing
                                                                    ? `${pricing.unit} - ${pricing.price}円`
                                                                    : 'Pricing Loading...'}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </Table.Cell>
                                            <Table.Cell>{preOrder.totalQuantity}</Table.Cell>
                                            <Table.Cell>{preOrderDate}</Table.Cell>
                                            <Table.Cell>
                                                <div>{preOrder.phone}</div>
                                                <div className="text-sm text-gray-500">
                                                    {preOrder.address}
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    )}
                </Card>
            </div>
        </Page>
    );
}
